import { useRef } from 'react';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import { api } from '../../api/client';
import { editorExtensions } from './extensions';
import { useImageUpload } from './useImageUpload';
import Toolbar from './Toolbar';
import EditorBubbleMenu from './EditorBubbleMenu';
import MediaPickerDrawer from '../media-picker/MediaPickerDrawer';
import { ImagePickerContext } from './image-picker-context';
import { useInlineImages } from './useInlineImages';

interface Props {
  /** 初始內容：Tiptap JSON 字串，或（遷移文章）HTML 字串。 */
  initialJson: string | null;
  initialHtml: string | null;
  onChange?: (json: string) => void;
  /** 唯讀預覽（無工具列）。 */
  readOnly?: boolean;
  /** 圖片加入內文後 → 立刻存檔（不等自動儲存的 debounce）。 */
  onImageInserted?: () => void;
}

function initialContent(json: string | null, html: string | null): object | string {
  if (json) {
    try {
      return JSON.parse(json) as object;
    } catch {
      /* fall through */
    }
  }
  return html ?? '';
}

/** 所見即所得編輯器；支援貼上／拖放圖片上傳 R2、圖片對齊圖說、表格、嵌入。 */
export default function Tiptap({
  initialJson,
  initialHtml,
  onChange,
  readOnly = false,
  onImageInserted,
}: Props) {
  const editorRef = useRef<Editor | null>(null);
  const { handlePaste, handleDrop, insertFiles } = useImageUpload(api.uploadImage, onImageInserted);
  const images = useInlineImages(editorRef, insertFiles, api.uploadImage, onImageInserted);

  const editor = useEditor({
    editable: !readOnly,
    extensions: editorExtensions,
    content: initialContent(initialJson, initialHtml),
    onUpdate: ({ editor: e }) => onChange?.(JSON.stringify(e.getJSON())),
    editorProps: readOnly
      ? {}
      : {
          handlePaste: (_view, event) =>
            editorRef.current ? handlePaste(editorRef.current, event) : false,
          handleDrop: (_view, event) =>
            editorRef.current ? handleDrop(editorRef.current, event as DragEvent) : false,
        },
  });

  editorRef.current = editor;
  if (!editor) return null;

  const content = (
    <EditorContent
      editor={editor}
      className="tiptap-content min-h-[420px] px-5 py-4 [&_.ProseMirror]:min-h-[400px] [&_.ProseMirror]:outline-none"
    />
  );
  // 唯讀預覽：不提供工具列、挑圖 Drawer 與換圖入口（context 為 null）
  if (readOnly) return <div className="rounded-lg border border-gray-200 bg-white">{content}</div>;

  return (
    <ImagePickerContext.Provider value={images.api}>
      <div className="rounded-lg border border-gray-200 bg-white">
        <Toolbar editor={editor} />
        <EditorBubbleMenu editor={editor} />
        {content}
        <MediaPickerDrawer {...images.drawer} />
      </div>
    </ImagePickerContext.Provider>
  );
}
